import React, { FunctionComponent, useState } from "react";
import { Modal, Upload, Progress, message } from "antd";
import { HiOutlineUpload, HiOutlineInbox, HiOutlineDocument, HiOutlineTrash } from "react-icons/hi";
import type { RcFile } from "antd/lib/upload";
import InfyButton from "../../InfyButton/InfyButton";
import { getFileIconUrl } from "./fileIcons";
import styles from "./FileUploadModal.module.scss";

const { Dragger } = Upload;

interface FileUploadModalProps {
  open: boolean;
  onClose: () => void;
  accept?: string;
  acceptLabel?: string;
  maxFileSize?: number; // bytes
  allowedExtensions?: string[]; // lowercase, no dot; when set, other file types are rejected
  upload: (file: File, onProgress: (percent: number) => void) => Promise<string>;
  onUploaded: (url: string, fileName: string) => void;
}

const FileUploadModal: FunctionComponent<FileUploadModalProps> = ({
  open,
  onClose,
  accept,
  acceptLabel,
  maxFileSize,
  allowedExtensions,
  upload,
  onUploaded
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);

  const reset = () => {
    setFile(null);
    setUploading(false);
    setProgress(0);
  };

  const handleCancel = () => {
    if (uploading) return;
    reset();
    onClose();
  };

  // Return false so antd holds the file instead of auto-uploading; we upload on the button click.
  const beforeUpload = (f: RcFile) => {
    const ext = (f.name.split(".").pop() || "").toLowerCase();
    if (allowedExtensions && !allowedExtensions.includes(ext)) {
      message.error(`Only ${acceptLabel || allowedExtensions.join(", ").toUpperCase()} files are accepted`);
      return Upload.LIST_IGNORE;
    }
    if (maxFileSize && f.size > maxFileSize) {
      message.error(`File must be smaller than ${Math.round(maxFileSize / (1024 * 1024))} MB`);
      return Upload.LIST_IGNORE;
    }
    setFile(f);
    return false;
  };

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    setProgress(0);
    try {
      const url = await upload(file, setProgress);
      onUploaded(url, file.name);
      reset();
    } catch {
      message.error("Upload failed. Please try again.");
      setUploading(false);
    }
  };

  const footer = (
    <div className="flex items-center justify-end gap-3">
      <InfyButton type="ghost" bicolor="primary" onClick={handleCancel} disabled={uploading}>
        Cancel
      </InfyButton>
      <InfyButton type="primary" bicolor="primary" onClick={handleUpload} disabled={!file} loading={uploading}>
        {uploading ? "Uploading…" : "Upload"}
      </InfyButton>
    </div>
  );

  return (
    <Modal
      open={open}
      onCancel={handleCancel}
      title={
        <span className="flex items-center gap-2 text-neutral-12 font-semibold">
          <HiOutlineUpload className="text-base" /> Upload file
        </span>
      }
      footer={footer}
      destroyOnClose
      maskClosable={false}
      width={520}
      centered
      className={styles.roundedModal}
    >
      <div className="flex flex-col gap-3">
        <Dragger accept={accept} multiple={false} maxCount={1} showUploadList={false} beforeUpload={beforeUpload} disabled={uploading}>
          <p className="flex justify-center text-primary-6 mb-2 text-4xl">
            <HiOutlineInbox />
          </p>
          <p className="m-0 text-sm font-medium text-neutral-11">Click or drag a file here to upload</p>
          {acceptLabel && <p className="m-0 mt-1 text-xs text-neutral-8">Accepted: {acceptLabel}</p>}
        </Dragger>

        {file && !uploading && (
          <div className="flex items-center gap-2 text-sm text-neutral-10">
            {getFileIconUrl(file.name) ? (
              <img src={getFileIconUrl(file.name)} alt="" className="flex-shrink-0 h-4 w-4 object-contain" />
            ) : (
              <HiOutlineDocument className="flex-shrink-0 text-neutral-9 text-base" />
            )}
            <span className="font-medium truncate">{file.name}</span>
            <button type="button" onClick={() => setFile(null)} aria-label="Remove selected file" className="flex-shrink-0 text-error-1 focus:outline-none">
              <HiOutlineTrash className="text-base" />
            </button>
          </div>
        )}

        {uploading && <Progress percent={progress} status="active" strokeColor="var(--primary-6)" />}
      </div>
    </Modal>
  );
};

export default FileUploadModal;
